import { EQUIPMENT_DOCK_HANG_RATIO } from '../../../../../utils/stockerDocks';
import { KIT_FOUP_BASE_WIDTH, resolveKitFoupScale } from '../../types';
import {
  clampProcessLoadPortCount,
  fitProcessLoadPortBank,
  resolveLoadPortSeatX,
  type EquipmentPortPose,
  type Vec3,
} from '../equipmentPortTransfer';

/**
 * Reference-matched vertical zones for procedural process tools.
 * All sub-assemblies scale from EQUIPMENT_WIDTH / HEIGHT / DEPTH.
 */
export type FabToolLayout = {
  width: number;
  height: number;
  depth: number;
  baseH: number;
  bodyH: number;
  colW: number;
  /** Large smoked-glass process window (shifted left for status module). */
  glassW: number;
  glassH: number;
  glassX: number;
  glassY: number;
  glassZ: number;
  chamberDepth: number;
  /** Tall vertical HMI on right-front. */
  panelW: number;
  panelH: number;
  panelX: number;
  panelY: number;
  panelZ: number;
  loadBankZ: number;
  loadDeckY: number;
  /** Deck centre Z inside the load-bank group (forward toward the rail). */
  loadDeckFrontZ: number;
  topCapY: number;
  topModuleH: number;
};

export function resolveFabToolLayout(width: number, height: number, depth: number): FabToolLayout {
  const baseH = height * 0.07;
  const bodyH = height - baseH;
  const glassW = width * 0.56;
  const glassH = bodyH * 0.47;
  const glassX = -width * 0.05;
  const glassY = baseH + bodyH * 0.64;
  const glassZ = depth * 0.47;
  const panelW = width * 0.2;
  const panelH = bodyH * 0.5;
  const panelX = width * 0.39;
  const panelY = baseH + bodyH * 0.61;
  const panelZ = depth * 0.41;
  return {
    width,
    height,
    depth,
    baseH,
    bodyH,
    colW: width * 0.042,
    glassW,
    glassH,
    glassX,
    glassY,
    glassZ,
    chamberDepth: depth * 0.2,
    panelW,
    panelH,
    panelX,
    panelY,
    panelZ,
    loadBankZ: depth * 0.3,
    loadDeckY: baseH + bodyH * 0.11,
    /** Forward extension — OHT drops FOUP outside the cabinet front face. */
    loadDeckFrontZ: depth * 0.36,
    topCapY: baseH + bodyH * 0.955,
    topModuleH: bodyH * 0.1,
  };
}

/** FOUP seat on the reference load deck — equipment-local. */
export function resolveFabLoadPortFoupPose(layout: FabToolLayout): Vec3 {
  const deckH = layout.height * 0.028;
  return [
    0,
    layout.loadDeckY + deckH * 0.85,
    layout.loadBankZ + layout.loadDeckFrontZ,
  ];
}

/** World Y the OHT hoist must reach — matches ReferenceLoadPlatform FOUP foot. */
export function resolveEquipmentLoadPortFoupSeatY(
  width: number,
  height: number,
  depth: number
): number {
  return resolveFabLoadPortFoupPose(resolveFabToolLayout(width, height, depth))[1];
}

/** FOUP rest position in the upper chamber (visible through blue front glass). */
export function resolveFabChamberFoupPose(layout: FabToolLayout): Vec3 {
  const upperGlassY = layout.glassH * 0.23;
  const frontZ = layout.chamberDepth * 0.16;
  return [
    layout.glassX,
    layout.glassY + upperGlassY * 0.55,
    layout.glassZ + frontZ - layout.chamberDepth * 0.1,
  ];
}

/** Distinct chamber slot positions for multi-FOUP equipment (capacity-driven). */
export function resolveFabChamberFoupPositions(
  layout: FabToolLayout,
  capacity: number
): Vec3[] {
  const base = resolveFabChamberFoupPose(layout);
  const slots = Math.max(1, Math.min(6, Math.floor(capacity)));
  if (slots <= 1) return [base];
  const spread = layout.width * 0.2;
  return Array.from({ length: slots }, (_, i) => {
    const t = slots === 1 ? 0.5 : i / (slots - 1);
    const xOffset = (t - 0.5) * spread * 2;
    return [base[0] + xOffset, base[1], base[2]] as Vec3;
  });
}

/**
 * Transfer poses aligned with ReferenceLoadPlatform + ProcessChamber geometry.
 * Internal dock is the upper chamber behind the blue glass panel.
 */
export function getFabProcessToolPortPoses(
  layout: FabToolLayout,
  loadPortCount: number,
  portFractions?: readonly number[]
): EquipmentPortPose[] {
  const { width, height, depth } = layout;
  const ports = clampProcessLoadPortCount(loadPortCount);
  const bank = fitProcessLoadPortBank(ports, width);
  const pitch = bank.pitch;
  const foupSize = KIT_FOUP_BASE_WIDTH * resolveKitFoupScale(width, height, depth);
  const shutterW = Math.min(width * 0.42, Math.max(foupSize * 1.35, width * 0.28));
  const shutterH = Math.min(height * 0.22, Math.max(foupSize * 1.2, height * 0.14));
  const shutterD = Math.max(0.04, layout.chamberDepth * 0.06);
  const loadPortBase = resolveFabLoadPortFoupPose(layout);
  const internal = resolveFabChamberFoupPose(layout);
  const seatZ = depth * EQUIPMENT_DOCK_HANG_RATIO;
  const frontZ = layout.chamberDepth * 0.16;

  return Array.from({ length: ports }, (_, i) => {
    const x = resolveLoadPortSeatX(
      portFractions?.[i],
      width,
      bank.nestWidth,
      (i - (ports - 1) / 2) * pitch
    );
    const loadPort: Vec3 = [x, loadPortBase[1], loadPortBase[2]];
    const ohtHandoff: Vec3 = [x, loadPort[1], seatZ];
    const chamberInternal: Vec3 = [internal[0] + x, internal[1], internal[2]];
    const shutter: Vec3 = [layout.glassX + x, layout.glassY, layout.glassZ + frontZ];
    return {
      bay: [x, 0, layout.loadBankZ],
      ohtHandoff,
      loadPort,
      internal: chamberInternal,
      shutter,
      shutterSize: [shutterW, shutterH, shutterD],
      shutterOpenTravel: shutterH * 1.04,
    };
  });
}

/** Integrated load-port module height (~0.7–0.9 m at default scale). */
export function resolveLoadPortModuleHeight(equipmentHeight: number): number {
  return equipmentHeight * 0.17;
}
